/**
 * 액체(Liquid) 사이징.
 *
 * 근거: API Standard 520 Part I, 10판(2020) 5.8 · 5.9절
 *
 *  인증 밸브 (5.8, ASME 용량인증 필요)
 *   - 식 (33) SI  A = 11.78·Q·√(Gl/(P1−P2)) / (Kd·Kw·Kc·Kv),  Kd = 0.65
 *   - 식 (37) SI  Re = Q·(18,800·Gl) / (μ·√A_selected)      μ: cP
 *   - 식 (38) SI  Re = 85,220·Q / (U·√A_selected)           U: Saybolt universal seconds
 *   - 식 (34)     Kv = (170/ReL + 1)^(−0.5),  ReL ≥ 80 에서 적용
 *   - 5.8.1.4~5   점도보정 반복 절차 (아래 참조)
 *
 *  비인증 밸브 (5.9, 용량인증이 없거나 불필요한 경우)
 *   - 식 (43) SI  A = 11.78·Q·√(Gl/(1.25·Ps−P2)) / (Kd·Kw·Kc·Kv·Kp),  Kd = 0.62
 *   - Kp: 25 % 과압에서 1.0, 그 외는 Figure 39. 10 % 과압이면 0.6 (5.9.2)
 *
 * ⚠ 압력 단위가 기체·수증기와 다르다. **액체 식의 P1·P2·Ps 는 게이지압(kPag)** 이다.
 *    기체 식 (9)·(19) 와 수증기 식 (26) 은 절대압(kPa abs)을 쓴다. 섞지 말 것.
 *
 * 단위: 유량 L/min · 압력 kPag · 면적 mm² · 점도 cP 또는 SSU
 */
import { API_526_ORIFICES, type Api526Orifice } from "../data/api526";
import type { ValveType } from "./gas";

/** 인증 밸브의 예비 사이징용 유효 배출계수 (5.8.1.3). */
export const KD_LIQUID_CERTIFIED = 0.65;
/** 비인증 밸브의 배출계수 (5.9.1). 표준이 0.62 로 고정한다. */
export const KD_LIQUID_NON_CERTIFIED = 0.62;

/** 이 점도 이하에서는 Kv = 1.0 으로 둔다 (5.8.1.3). */
export const VISCOSITY_CORRECTION_THRESHOLD_CP = 100;

/** 식 (34) 의 적용 하한. 이보다 낮으면 Figure 38 을 읽어야 한다. */
export const KV_MIN_REYNOLDS = 80;

/** 식 (38) 이 권장되지 않는 SSU 하한. */
export const SSU_MIN_FOR_EQUATION = 100;

/** 점도 입력. 절대점도(cP) 또는 Saybolt universal seconds 중 하나. */
export type Viscosity =
  | { readonly kind: "cP"; readonly value: number }
  | { readonly kind: "SSU"; readonly value: number };

interface LiquidSizingCommon {
  /** Q — 배출 온도에서의 소요 배출량 (L/min) */
  readonly requiredFlowLpm: number;
  /** Gl — 배출 온도에서의 비중 (표준조건 물 기준) */
  readonly specificGravity: number;
  /** P2 — 총 배압 (kPag, 게이지) */
  readonly backpressureKpaGauge: number;
  readonly valveType: ValveType;
  /** 점도. 생략하면 점도보정을 하지 않는다(Kv = 1.0). */
  readonly viscosity?: Viscosity;
  /** Kd — 생략 시 인증 0.65 / 비인증 0.62 */
  readonly dischargeCoefficient?: number;
  /**
   * Kw — 배압 보정계수. **밸런스드 벨로우즈에만 적용**된다(5.8.1.3).
   * Figure 32 는 그래프이므로 프로그램이 추정하지 않는다. 제작사 자료를 쓸 것.
   */
  readonly bellowsBackpressureCorrection?: number;
  /** Kc — 파열판 없음 1.0 / 인증값 없는 조합 0.9 */
  readonly combinationCorrection?: number;
}

export type LiquidSizingInput =
  | (LiquidSizingCommon & {
      readonly certification: "certified";
      /** P1 — 배출 압력 (kPag). 설정압 + 허용 과압. */
      readonly relievingPressureKpaGauge: number;
    })
  | (LiquidSizingCommon & {
      readonly certification: "non-certified";
      /** Ps — 설정압 (kPag) */
      readonly setPressureKpaGauge: number;
      /**
       * Kp — 과압 보정계수. 25 % 과압이면 1.0, 10 % 과압이면 0.6 (5.9.2).
       * 그 외 값은 Figure 39 에서 읽어 넣어야 한다.
       */
      readonly overpressureCorrection?: number;
    });

export interface LiquidSizingSuccess {
  readonly ok: true;
  /** A — 점도보정까지 반영한 최종 소요 면적 (mm²) */
  readonly requiredAreaMm2: number;
  readonly equation: "API 520 Part I (33)" | "API 520 Part I (43)";
  readonly intermediates: {
    /** AR — 점도보정 전(Kv = 1.0) 소요 면적 (mm²) */
    readonly uncorrectedAreaMm2: number;
    /** 유효 차압 (kPa): 인증 P1−P2, 비인증 1.25·Ps−P2 */
    readonly differentialPressureKpa: number;
    /** Re 계산에 쓴 표준 오리피스. 점도보정을 안 했으면 null. */
    readonly selectedOrificeForReynolds: Api526Orifice | null;
    readonly reynolds: number | null;
    readonly reynoldsEquation: "API 520 Part I (37)" | "API 520 Part I (38)" | null;
    readonly kv: number;
    readonly kd: number;
    readonly kw: number;
    readonly kc: number;
    /** Kp — 비인증 밸브에만 존재 */
    readonly kp: number | null;
    /** 점도보정 반복 횟수 (5.8.1.5). 보정 없으면 0. */
    readonly viscosityIterations: number;
  };
  readonly warnings: readonly string[];
}

export interface LiquidSizingFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "reynolds-too-low" | "exceeds-largest";
  readonly message: string;
}

export type LiquidSizingResult = LiquidSizingSuccess | LiquidSizingFailure;

/**
 * 점도 보정계수 Kv — API 520 Part I 식 (34).
 *
 *   Kv = ( 170/ReL + 1 ) ^ (−0.5)
 */
export function viscosityCorrection(reynolds: number): number {
  return Math.pow(170 / reynolds + 1, -0.5);
}

/**
 * 레이놀즈수 — 절대점도(cP) 기준, 식 (37) SI.
 *
 *   Re = Q·(18,800·Gl) / ( μ · √A_selected )
 */
export function reynoldsFromCentipoise(
  flowLpm: number,
  specificGravity: number,
  viscosityCp: number,
  selectedAreaMm2: number,
): number {
  return (
    (flowLpm * (18_800 * specificGravity)) /
    (viscosityCp * Math.sqrt(selectedAreaMm2))
  );
}

/**
 * 레이놀즈수 — Saybolt universal seconds 기준, 식 (38) SI.
 *
 *   Re = 85,220·Q / ( U · √A_selected )
 */
export function reynoldsFromSsu(
  flowLpm: number,
  ssu: number,
  selectedAreaMm2: number,
): number {
  return (85_220 * flowLpm) / (ssu * Math.sqrt(selectedAreaMm2));
}

function invalid(
  reason: LiquidSizingFailure["reason"],
  message: string,
): LiquidSizingFailure {
  return { ok: false, reason, message };
}

/** 주어진 면적 이상인 가장 작은 표준 오리피스. */
function nextOrificeAtOrAbove(areaMm2: number): Api526Orifice | undefined {
  return API_526_ORIFICES.find((o) => o.effectiveAreaMm2 >= areaMm2);
}

/** 액체 소요 배출 면적을 계산한다. */
export function sizeLiquid(input: LiquidSizingInput): LiquidSizingResult {
  const {
    requiredFlowLpm: Q,
    specificGravity: Gl,
    backpressureKpaGauge: P2,
    valveType,
  } = input;

  if (!Number.isFinite(Q) || Q <= 0) {
    return invalid("invalid-input", "소요 배출량 Q 는 0보다 커야 합니다.");
  }
  if (!Number.isFinite(Gl) || Gl <= 0) {
    return invalid("invalid-input", "비중 Gl 은 0보다 커야 합니다.");
  }
  if (!Number.isFinite(P2) || P2 < 0) {
    return invalid("invalid-input", "배압 P2 는 0 이상이어야 합니다(게이지압).");
  }

  // ── 유효 차압 ────────────────────────────────────────────────
  let dP: number;
  let Kp: number | null = null;
  let equation: LiquidSizingSuccess["equation"];

  const warnings: string[] = [];

  if (input.certification === "certified") {
    dP = input.relievingPressureKpaGauge - P2;
    equation = "API 520 Part I (33)";
    if (!Number.isFinite(input.relievingPressureKpaGauge)) {
      return invalid("invalid-input", "배출 압력 P1 이 올바르지 않습니다.");
    }
  } else {
    // 5.9: 1.25·Ps − P2 (25 % 과압을 전제로 한 식)
    dP = 1.25 * input.setPressureKpaGauge - P2;
    equation = "API 520 Part I (43)";
    Kp = input.overpressureCorrection ?? 1.0;
    if (!Number.isFinite(Kp) || Kp <= 0 || Kp > 1) {
      return invalid("invalid-input", "Kp 는 0 초과 1 이하이어야 합니다.");
    }
    if (input.overpressureCorrection === undefined) {
      warnings.push(
        "Kp 가 입력되지 않아 1.0(25 % 과압 기준)으로 계산했습니다. " +
          "10 % 과압이면 0.6 을 써야 하며(5.9.2), 그 외 값은 Figure 39 에서 읽어야 합니다.",
      );
    }
    warnings.push(
      "비인증 밸브 방식은 10 % 과압 적용 시 통상 과대설계가 됩니다(5.9.2). " +
        "용량인증 밸브를 쓸 수 있으면 인증 방식(5.8)을 권장합니다.",
    );
  }

  if (dP <= 0) {
    return invalid(
      "invalid-input",
      "유효 차압이 0 이하입니다. 배압이 배출 압력 이상이면 유동이 발생하지 않습니다.",
    );
  }

  const Kd =
    input.dischargeCoefficient ??
    (input.certification === "certified"
      ? KD_LIQUID_CERTIFIED
      : KD_LIQUID_NON_CERTIFIED);
  const Kc = input.combinationCorrection ?? 1.0;
  if (!Number.isFinite(Kd) || Kd <= 0) return invalid("invalid-input", "Kd 가 올바르지 않습니다.");
  if (!Number.isFinite(Kc) || Kc <= 0) return invalid("invalid-input", "Kc 가 올바르지 않습니다.");

  // ── Kw: 벨로우즈에만 적용 (5.8.1.3) ─────────────────────────
  let Kw = 1.0;
  if (valveType === "balanced-bellows") {
    if (input.bellowsBackpressureCorrection === undefined) {
      warnings.push(
        "밸런스드 벨로우즈인데 배압 보정계수 Kw 가 입력되지 않아 1.0 으로 계산했습니다. " +
          "제작사 자료(또는 API 520 Part I Figure 32)에서 확인해 입력해야 합니다.",
      );
    } else {
      Kw = input.bellowsBackpressureCorrection;
      if (!Number.isFinite(Kw) || Kw <= 0 || Kw > 1) {
        return invalid("invalid-input", "Kw 는 0 초과 1 이하이어야 합니다.");
      }
    }
  } else if (input.bellowsBackpressureCorrection !== undefined) {
    warnings.push(
      "일반형·파일럿형에는 배압 보정계수 Kw 가 적용되지 않습니다(5.8.1.3). " +
        "1.0 으로 계산했습니다.",
    );
  }

  /** 식 (33)/(43) 을 주어진 Kv 로 푼다. */
  const areaFor = (Kv: number): number =>
    ((11.78 * Q) / (Kd * Kw * Kc * Kv * (Kp ?? 1.0))) * Math.sqrt(Gl / dP);

  // ── 1단계: 점도보정 없이 AR (5.8.1.4) ───────────────────────
  const AR = areaFor(1.0);

  const visc = input.viscosity;
  const needsViscosityCorrection =
    visc !== undefined &&
    (visc.kind === "cP"
      ? visc.value > VISCOSITY_CORRECTION_THRESHOLD_CP
      : // SSU 는 cP 로 바로 비교할 수 없다. 식 (38) 의 권장 하한만 확인한다.
        visc.value >= SSU_MIN_FOR_EQUATION);

  if (visc !== undefined && !Number.isFinite(visc.value)) {
    return invalid("invalid-input", "점도 값이 올바르지 않습니다.");
  }
  if (visc !== undefined && visc.kind === "SSU" && visc.value < SSU_MIN_FOR_EQUATION) {
    warnings.push(
      `점도 ${visc.value} SSU 는 식 (38) 의 권장 하한 ${SSU_MIN_FOR_EQUATION} SSU 미만이라 ` +
        "점도보정을 적용하지 않았습니다(Kv = 1.0).",
    );
  }
  if (
    visc !== undefined &&
    visc.kind === "cP" &&
    visc.value <= VISCOSITY_CORRECTION_THRESHOLD_CP
  ) {
    warnings.push(
      `점도 ${visc.value} cP 는 ${VISCOSITY_CORRECTION_THRESHOLD_CP} cP 이하라 ` +
        "Kv = 1.0 으로 두었습니다(5.8.1.3).",
    );
  }

  if (!needsViscosityCorrection) {
    if (AR > API_526_ORIFICES[API_526_ORIFICES.length - 1]!.effectiveAreaMm2) {
      return invalid(
        "exceeds-largest",
        `소요 면적 ${AR.toFixed(0)} mm² 가 표준 최대 오리피스를 넘습니다. 병렬 설치를 검토하십시오.`,
      );
    }
    return {
      ok: true,
      requiredAreaMm2: AR,
      equation,
      intermediates: {
        uncorrectedAreaMm2: AR,
        differentialPressureKpa: dP,
        selectedOrificeForReynolds: null,
        reynolds: null,
        reynoldsEquation: null,
        kv: 1.0,
        kd: Kd,
        kw: Kw,
        kc: Kc,
        kp: Kp,
        viscosityIterations: 0,
      },
      warnings,
    };
  }

  // ── 2~5단계: 점도보정 반복 (5.8.1.4~5) ──────────────────────
  //  AR 보다 큰 표준 오리피스를 골라 Re → Kv → A 를 구하고,
  //  보정된 A 가 그 오리피스를 넘으면 한 단계 키워 다시 계산한다.
  const reynoldsEquation =
    visc.kind === "cP" ? "API 520 Part I (37)" : "API 520 Part I (38)";

  let candidate = nextOrificeAtOrAbove(AR);
  let iterations = 0;

  while (candidate) {
    iterations += 1;
    const Aselected = candidate.effectiveAreaMm2;
    const Re =
      visc.kind === "cP"
        ? reynoldsFromCentipoise(Q, Gl, visc.value, Aselected)
        : reynoldsFromSsu(Q, visc.value, Aselected);

    if (Re < KV_MIN_REYNOLDS) {
      return invalid(
        "reynolds-too-low",
        `레이놀즈수 ${Re.toFixed(0)} 은 식 (34) 의 적용 하한 ${KV_MIN_REYNOLDS} 미만입니다. ` +
          "Figure 38 에서 Kv 를 직접 읽어 적용하거나, 점성 유체 전용 검토가 필요합니다.",
      );
    }

    const Kv = viscosityCorrection(Re);
    const A = areaFor(Kv);

    // 보정 결과가 이 오리피스에 들어가면 확정 (5.8.1.5)
    if (A <= Aselected) {
      return {
        ok: true,
        requiredAreaMm2: A,
        equation,
        intermediates: {
          uncorrectedAreaMm2: AR,
          differentialPressureKpa: dP,
          selectedOrificeForReynolds: candidate,
          reynolds: Re,
          reynoldsEquation,
          kv: Kv,
          kd: Kd,
          kw: Kw,
          kc: Kc,
          kp: Kp,
          viscosityIterations: iterations,
        },
        warnings,
      };
    }

    // 넘치면 다음 등급으로
    const idx = API_526_ORIFICES.indexOf(candidate);
    candidate = API_526_ORIFICES[idx + 1];
  }

  return invalid(
    "exceeds-largest",
    "점도보정을 반영하면 표준 최대 오리피스로도 부족합니다. 병렬 설치를 검토하십시오.",
  );
}
